import type {SortDirection} from '../helpers'

// Enums

export enum RiaStatus {
  New = 'New',
  Pending = 'Pending',
  InProgress = 'InProgress',
  Completed = 'Completed',
  Waived = 'Waived',
  Rejected = 'Rejected'
}

export enum RiaPriority {
  A = 'A',
  B = 'B',
  C = 'C'
}

export enum CostClassification {
  OperationalCosts = 'OperationalCosts',
  CapitalExpenditure = 'CapitalExpenditure',
  HumanElementLowCosts = 'HumanElementLowCosts'
}

// Response DTOs

export type PaginatedResponse<T> = {
  items: T[]
  totalCount: number
  page: number
  pageSize: number
  totalPages: number
}

export type ReportSummaryDto = {
  id: string
  upmId: string
  plantId: string
  country?: string | null
  siteName: string
  location?: string | null
  // The creator's user id (JWT `user_id`). Optional on the client so existing
  // fixtures/mocks need not set it; the API always provides it.
  uploadedByUserId?: string
  uploadedByUserName: string
  uploadedAt: string
  riaCount: number
  totalLePrior?: number | null
  totalLeAfter?: number | null
  totalLeDelta?: number | null
  // Optional to keep existing fixtures/tests backward compatible while backend rollout completes.
  linkCount?: number
  statusBreakdown: Record<string, number>
  isArchived: boolean
}

export type ReportLinkDto = {
  id: string
  url: string
  label: string
  addedByUserName: string
  addedAt: string
}

export type RiaListItemDto = {
  id: string
  upmId: string
  reportId: string
  riNumber: string
  dateIssued: string
  title: string
  priority: RiaPriority
  costClassification: CostClassification
  indicativeTimeScale?: string | null
  lePrior?: number | null
  leAfter?: number | null
  leDelta?: number | null
  status: RiaStatus
  commentCount: number
  lastStatusChange?: string | null
  createdAt: string
  updatedAt: string
}

export type RiaDetailDto = RiaListItemDto & {
  plantId: string
  siteName: string
  // Detail-only fields (not present on the RIA list/table).
  responsiblePersonUserId?: string | null
  // Name snapshot taken when the person was assigned.
  responsiblePersonName?: string | null
  dueDate?: string | null
}

// A user with Risk Survey access to a plant, eligible as responsible person.
export type UserSuggestionDto = {
  userId: string
  name: string
}

export type RiaTotals = {
  totalLePrior?: number | null
  totalLeAfter?: number | null
  totalLeDelta?: number | null
}

// RIA list response: a paginated page plus LE totals computed across the whole
// filtered set (not just the current page), used for the table subtotal row.
// `totals` is optional so the client tolerates responses from a backend that
// has not yet been updated to return it.
export type RiaListResponse = PaginatedResponse<RiaListItemDto> & {
  totals?: RiaTotals
}

export type CommentDto = {
  id: string
  userName: string
  commentText: string
  createdAt: string
  editedAt?: string | null
}

export type AuditLogEntryDto = {
  id: string
  previousStatus?: RiaStatus | null
  newStatus: RiaStatus
  changedByUserName: string
  changedAt: string
  reason?: string | null
}

export type RiaDetailsAuditLogEntryDto = {
  id: string
  previousResponsiblePersonUserId?: string | null
  newResponsiblePersonUserId?: string | null
  previousResponsiblePersonName?: string | null
  newResponsiblePersonName?: string | null
  previousDueDate?: string | null
  newDueDate?: string | null
  changedByUserName: string
  changedAt: string
}

export enum ReportAuditAction {
  Archived = 'Archived',
  Unarchived = 'Unarchived'
}

export type ReportAuditLogEntryDto = {
  id: string
  action: ReportAuditAction
  performedByUserName: string
  performedAt: string
  reason?: string | null
}

// Request DTOs

export type CreateReportRequest = {
  upmId: string
  siteName: string
  location?: string | null
  totalLePrior?: number | null
  totalLeAfter?: number | null
  riskImprovementActions: CreateRiaRequest[]
}

// Correct the plant/site a report belongs to (e.g. uploaded to the wrong plant).
// Only the original creator may perform this update.
export type UpdateReportSiteRequest = {
  upmId: string
  siteName: string
  location?: string | null
}

export type ImportDuplicateCheckRequest = {
  upmId: string
  siteName: string
  riNumbers: string[]
}

export type ImportDuplicateCheckResponseDto = {
  hasPotentialDuplicate: boolean
  isExactDuplicate: boolean
  matchingReportId?: string | null
  incomingRiaCount: number
  matchingReportRiaCount: number
  overlappingRiaCount: number
  overlappingRiNumbers: string[]
}

export type CreateRiaRequest = {
  riNumber: string
  dateIssued: string
  title: string
  priority: RiaPriority
  costClassification: CostClassification
  indicativeTimeScale?: string | null
  lePrior?: number | null
  leAfter?: number | null
}

export type UpdateRiaStatusRequest = {
  status: RiaStatus
  reason?: string | null
}

export type UpdateRiaDetailsRequest = {
  responsiblePersonUserId?: string | null
  dueDate?: string | null
}

export type CreateCommentRequest = {
  commentText: string
}

export type AddReportLinkRequest = {
  url: string
  label: string
}

// Query filter

export type RiaQueryFilter = {
  reportId?: string
  upmId?: string
  status?: RiaStatus[]
  priority?: RiaPriority[]
  search?: string
  page: number
  pageSize: number
  sortBy?: string
  sortDir?: SortDirection
}
